import { ChangeEvent, useEffect, useState } from 'react';
import { v4 as generateId } from 'uuid';
import container from 'src/container';
import { Creator } from 'src/domain/creator';
import avatarDefault from 'src/assets/images/client_default.png';
import { useAvatar } from './useAvatar';

const {
    cradle: { munduClient, storageService },
} = container;

export const useCreatorAvatarEditor = (creator: Creator) => {
    const { src } = useAvatar(creator.profile.avatar);
    const [file, setFile] = useState<File>();
    const [removed, setRemoved] = useState(false);
    const [filePreview, setFilePreview] = useState<string>();

    useEffect(() => {
        if (!file) {
            setFilePreview(undefined);
            return;
        }
        const url = URL.createObjectURL(file);
        setFilePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextFile = event.target.files?.[0];
        if (!nextFile) return;
        setFile(nextFile);
        setRemoved(false);
        event.target.value = '';
    };

    const remove = () => {
        setFile(undefined);
        setRemoved(true);
    };

    const getAvatarUpdate = async (): Promise<string | null | undefined> => {
        if (removed) return null;
        if (!file) return undefined;
        const sas = await munduClient.getAvatarSas();
        const avatar = generateId();
        await storageService.uploadImage(file, avatar, sas);
        return avatar;
    };

    const reset = () => {
        setFile(undefined);
        setRemoved(false);
    };

    return {
        preview: removed ? avatarDefault : filePreview ?? src,
        hasPicture: !removed && Boolean(file || creator.profile.avatar),
        hasChanges: Boolean(file) || removed,
        onChange,
        remove,
        getAvatarUpdate,
        reset,
    };
};
